import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Search, ArrowRight, Layers } from 'lucide-react';
import { trackTemplateUsed } from '@/lib/analytics';
import SEOHead from '@/components/SEOHead';

const CATEGORIES = [
  'All', 'Landing Page', 'Portfolio', 'Restaurant', 'E-Commerce',
  'Agency', 'Blog', 'SaaS', 'Personal', 'Nonprofit', 'Event',
];

const CATEGORY_GRADIENTS = {
  'Landing Page': 'from-blue-500/20 to-indigo-500/20 border-blue-500/20',
  'Portfolio':    'from-emerald-500/20 to-teal-500/20 border-emerald-500/20',
  'Restaurant':   'from-red-500/20 to-amber-500/20 border-red-500/20',
  'E-Commerce':   'from-pink-500/20 to-rose-500/20 border-pink-500/20',
  'Agency':       'from-violet-500/20 to-purple-500/20 border-violet-500/20',
  'Blog':         'from-orange-500/20 to-yellow-500/20 border-orange-500/20',
  'SaaS':         'from-indigo-500/20 to-blue-500/20 border-indigo-500/20',
  'Personal':     'from-slate-500/20 to-zinc-500/20 border-slate-500/20',
  'Nonprofit':    'from-teal-500/20 to-amber-500/20 border-teal-500/20',
  'Event':        'from-green-500/20 to-cyan-500/20 border-green-500/20',
};

const CATEGORY_BADGE = {
  'Landing Page': 'bg-blue-500/15 text-blue-400 border-blue-500/25',
  'Portfolio':    'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
  'Restaurant':   'bg-red-500/15 text-red-400 border-red-500/25',
  'E-Commerce':   'bg-pink-500/15 text-pink-400 border-pink-500/25',
  'Agency':       'bg-violet-500/15 text-violet-400 border-violet-500/25',
  'Blog':         'bg-orange-500/15 text-orange-400 border-orange-500/25',
  'SaaS':         'bg-indigo-500/15 text-indigo-400 border-indigo-500/25',
  'Personal':     'bg-slate-500/15 text-slate-400 border-slate-500/25',
  'Nonprofit':    'bg-teal-500/15 text-teal-400 border-teal-500/25',
  'Event':        'bg-green-500/15 text-green-400 border-green-500/25',
};

function TemplateCard({ template, onUse, index }) {
  const cardGradient = CATEGORY_GRADIENTS[template.category] || 'from-zinc-500/20 to-zinc-600/20 border-zinc-500/20';
  const badge        = CATEGORY_BADGE[template.category]    || 'bg-zinc-500/15 text-zinc-400 border-zinc-500/25';

  return (
    <div
     
     
     
      className="group relative bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden hover:border-zinc-600 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_40px_rgba(0,0,0,0.4)]"
    >
      {/* Gradient top band */}
      <div className={`h-32 bg-gradient-to-br ${cardGradient} border-b border-zinc-800 flex items-center justify-center relative overflow-hidden`}>
        <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')]" />
        <span className="text-5xl select-none">{template.icon}</span>
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <button
            onClick={() => onUse(template)}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-ink text-sm font-semibold px-5 py-2.5 rounded-full shadow-[0_0_20px_rgba(249,115,22,0.4)] transition-all scale-90 group-hover:scale-100"
          >
            
            Use Template
          </button>
        </div>
      </div>

      {/* Card body */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="text-white font-semibold text-sm leading-snug">{template.title}</h3>
          <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badge}`}>
            {template.category}
          </span>
        </div>
        <p className="text-zinc-500 text-xs leading-relaxed mb-4 line-clamp-2">{template.description}</p>
        <Button
          onClick={() => onUse(template)}
          size="sm"
          className="w-full h-8 text-xs bg-zinc-800 hover:bg-orange-500 hover:text-white text-zinc-300 rounded-xl border border-zinc-700 hover:border-orange-500 transition-all group/btn"
        >
          
          Use Template
          <ArrowRight className="w-3 h-3 ml-1.5 opacity-0 group-hover/btn:opacity-100 transition-opacity" />
        </Button>
      </div>
    </div>
  );
}

export default function Templates() {
  const navigate  = useNavigate();
  const { user }  = useAuth();

  const [templates, setTemplates]           = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch]                 = useState('');
  const [loading, setLoading]               = useState(true);

  useEffect(() => {
    const apiUrl = import.meta.env.VITE_API_URL ?? '';
    fetch(`${apiUrl}/api/templates`)
      .then(r => r.ok ? r.json() : { templates: [] })
      .then(data => {
        setTemplates(data.templates || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const filtered = templates.filter(t => {
    const matchCat    = activeCategory === 'All' || t.category === activeCategory;
    const matchSearch = !search || t.title.toLowerCase().includes(search.toLowerCase()) || t.description.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const handleUseTemplate = (template) => {
    if (!user) {
      toast.error('Please sign in to use a template.');
      return;
    }
    trackTemplateUsed({ title: template.title, category: template.category });
    navigate('/AIBuilder', { state: { templatePrompt: template.prompt } });
  };

  return (
    <div className="min-h-screen bg-zinc-950 pt-28 pb-20">
      <SEOHead
        title="Website Templates — Start in Seconds"
        description="Start with a professionally designed template across 10 categories. Let our AI customise it for you in seconds."
        ogUrl="https://builderslab.net/Templates"
      />
      {/* Background glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] bg-[radial-gradient(ellipse_at_top,rgba(249,115,22,0.06),transparent_70%)]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        {/* Header */}
        <div
         
         
         
          className="text-center mb-14"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-semibold uppercase tracking-widest mb-6">
            <Layers className="w-3.5 h-3.5" />
            22 Templates Across 10 Categories
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4 tracking-tighter">
            Start From a <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600">Template</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto leading-relaxed">
            Choose a starting point, then customise it with our AI — or use the prompt as inspiration for your own creation.
          </p>
        </div>

        {/* Search + Category filters */}
        <div
         
         
         
          className="flex flex-col gap-4 mb-10"
        >
          {/* Search */}
          <div className="relative max-w-md mx-auto w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search templates…"
              className="w-full bg-zinc-900 border border-zinc-800 rounded-full pl-11 pr-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-600 transition-colors"
            />
          </div>

          {/* Category pills */}
          <div className="flex flex-wrap justify-center gap-2">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                  activeCategory === cat
                    ? 'bg-orange-500 text-ink shadow-[0_0_15px_rgba(249,115,22,0.3)]'
                    : 'bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Results count */}
        {!loading && (
          <p className="text-zinc-600 text-sm mb-6 text-center">
            {filtered.length} template{filtered.length !== 1 ? 's' : ''}
            {activeCategory !== 'All' ? ` in ${activeCategory}` : ''}
            {search ? ` matching "${search}"` : ''}
          </p>
        )}

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden animate-pulse">
                <div className="h-32 bg-zinc-800" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-zinc-800 rounded w-3/4" />
                  <div className="h-3 bg-zinc-800 rounded w-full" />
                  <div className="h-3 bg-zinc-800 rounded w-5/6" />
                  <div className="h-8 bg-zinc-800 rounded-xl mt-3" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-zinc-500 text-lg mb-2">No templates found</p>
            <p className="text-zinc-600 text-sm">Try a different category or search term</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            <AnimatePresence mode="popLayout">
              {filtered.map((template, i) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onUse={handleUseTemplate}
                  index={i}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Bottom CTA */}
        <div
         
         
         
          className="text-center mt-16 pt-10 border-t border-zinc-900"
        >
          <p className="text-zinc-500 text-sm mb-4">
            Have a completely different idea? Start from scratch.
          </p>
          <Button
            onClick={() => navigate('/AIBuilder')}
            className="bg-orange-500 hover:bg-orange-400 text-ink rounded-full px-8 h-11 font-semibold shadow-[0_0_20px_rgba(249,115,22,0.25)]"
          >
            
            Open AI Builder
          </Button>
        </div>
      </div>
    </div>
  );
}
